import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Sparkles,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    AlertCircle,
    ArrowRight,
    Send,
    MessageSquare,
    Target,
    Loader2,
    Check,
    X,
    Zap
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import {
    sendAdvisorMessage,
    createUserMessage,
    createAssistantMessage,
    executeAction,
    GOAL_PRESETS
} from '../../services/advisorChatService';
import { buildSmartContext, estimateTokenCount } from '../../services/smartContextBuilder';
import { buildCampaignKnowledgeBase } from '../../services/campaignKnowledgeService';

/**
 * Simple markdown renderer for chat messages
 * Handles: **bold**, *italic*, `code`, bullet points, numbered lists, headers
 */
const renderMarkdown = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = null;

    const processInlineFormatting = (line, index) => {
        const parts = [];
        let remaining = line;
        let partIndex = 0;

        while (remaining.length > 0) {
            const codeMatch = remaining.match(/`([^`]+)`/);
            const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
            const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);

            let earliestMatch = null;
            let earliestIndex = remaining.length;
            let matchType = null;

            if (codeMatch && remaining.indexOf(codeMatch[0]) < earliestIndex) {
                earliestMatch = codeMatch;
                earliestIndex = remaining.indexOf(codeMatch[0]);
                matchType = 'code';
            }
            if (boldMatch && remaining.indexOf(boldMatch[0]) < earliestIndex) {
                earliestMatch = boldMatch;
                earliestIndex = remaining.indexOf(boldMatch[0]);
                matchType = 'bold';
            }
            if (italicMatch && remaining.indexOf(italicMatch[0]) < earliestIndex) {
                earliestMatch = italicMatch;
                earliestIndex = remaining.indexOf(italicMatch[0]);
                matchType = 'italic';
            }

            if (earliestMatch) {
                if (earliestIndex > 0) {
                    parts.push(remaining.substring(0, earliestIndex));
                }
                if (matchType === 'code') {
                    parts.push(
                        <code key={`${index}-${partIndex++}`} style={{
                            background: 'rgba(0,0,0,0.1)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '0.9em'
                        }}>
                            {earliestMatch[1]}
                        </code>
                    );
                } else if (matchType === 'bold') {
                    parts.push(<strong key={`${index}-${partIndex++}`}>{earliestMatch[1]}</strong>);
                } else if (matchType === 'italic') {
                    parts.push(<em key={`${index}-${partIndex++}`}>{earliestMatch[1]}</em>);
                }
                remaining = remaining.substring(earliestIndex + earliestMatch[0].length);
            } else {
                parts.push(remaining);
                break;
            }
        }
        return parts.length > 0 ? parts : line;
    };

    const flushList = () => {
        if (listItems.length > 0) {
            const ListTag = listType === 'ordered' ? 'ol' : 'ul';
            elements.push(
                <ListTag key={`list-${elements.length}`} style={{
                    margin: 'var(--space-2) 0',
                    paddingLeft: 'var(--space-4)',
                    listStyleType: listType === 'ordered' ? 'decimal' : 'disc'
                }}>
                    {listItems}
                </ListTag>
            );
            listItems = [];
            listType = null;
        }
    };

    lines.forEach((line, index) => {
        const bulletMatch = line.match(/^[\s]*[-•]\s+(.+)/);
        const numberedMatch = line.match(/^[\s]*(\d+)[.)]\s+(.+)/);

        if (bulletMatch) {
            if (listType !== 'unordered') {
                flushList();
                listType = 'unordered';
            }
            listItems.push(
                <li key={`li-${index}`} style={{ marginBottom: 'var(--space-1)' }}>
                    {processInlineFormatting(bulletMatch[1], index)}
                </li>
            );
        } else if (numberedMatch) {
            if (listType !== 'ordered') {
                flushList();
                listType = 'ordered';
            }
            listItems.push(
                <li key={`li-${index}`} style={{ marginBottom: 'var(--space-1)' }}>
                    {processInlineFormatting(numberedMatch[2], index)}
                </li>
            );
        } else {
            flushList();
            if (line.trim() === '') {
                elements.push(<br key={`br-${index}`} />);
            } else {
                elements.push(
                    <p key={`p-${index}`} style={{ margin: '0 0 var(--space-2) 0' }}>
                        {processInlineFormatting(line, index)}
                    </p>
                );
            }
        }
    });

    flushList();
    return elements;
};

/**
 * AIAdvisorPanel - Quick Mode conversational advisor for Command Center
 * Shows latest message, goal, quick actions, and link to Deep Dive
 */
const AIAdvisorPanel = ({
    insights = [],
    metrics,
    previousMetrics,
    aggregateTrends,
    previousPeriodData = {},
    overallROAS = 0,
    loading,
    campaigns = [],
    dateRangeLabel = 'this period',
    // New props for conversational mode
    userGoal,
    onSetGoal,
    messages = [],
    onMessagesChange,
    chatId,
    userId,
    brandId
}) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [showInput, setShowInput] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [actionPending, setActionPending] = useState(false);
    const inputRef = useRef(null);

    // Safe metrics with defaults
    const safeMetrics = metrics || { spend: 0, revenue: 0, impressions: 0, clicks: 0, conversions: 0 };
    const activeCampaigns = campaigns.filter(c => c.status?.toUpperCase() === 'ACTIVE');

    // Get latest assistant message
    const latestAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');

    // Focus input when shown
    useEffect(() => {
        if (showInput && inputRef.current) {
            inputRef.current.focus();
        }
    }, [showInput]);

    // Pre-compute knowledge base for efficient context building
    const knowledgeBase = useMemo(() => {
        return buildCampaignKnowledgeBase(campaigns, previousPeriodData);
    }, [campaigns, previousPeriodData]);

    // Build smart context based on the question
    // This significantly reduces token usage by only including relevant data
    const buildContext = (question) => {
        return buildSmartContext(question, {
            campaigns,
            aggregateMetrics: safeMetrics,
            previousMetrics,
            aggregateTrends,
            previousPeriodData,
            insights,
            dateRangeLabel,
            userGoal
        });
    };

    // Legacy full context builder (for initial goal-based messages)
    const buildFullContext = () => {
        // Helper to safely get metric value from multiple possible locations
        const getMetric = (campaign, metric) => {
            // Check all possible locations where metrics might be stored
            const metricsLocations = [
                campaign.currentMetrics,           // Normalized location (from backend)
                campaign.insights?.data?.[0],      // Raw Meta API location
                campaign.metrics,                  // Alternative location
                campaign                           // Directly on campaign object
            ];

            for (const metrics of metricsLocations) {
                if (!metrics) continue;

                // Try direct property
                if (metrics[metric] !== undefined && metrics[metric] !== null) {
                    const val = metrics[metric];
                    return typeof val === 'number' ? val : parseFloat(val) || 0;
                }

                // Try action_values_ prefix (Meta API specific)
                const actionKey = `action_values_${metric}`;
                if (metrics[actionKey] !== undefined && metrics[actionKey] !== null) {
                    const val = metrics[actionKey];
                    return typeof val === 'number' ? val : parseFloat(val) || 0;
                }
            }

            return 0;
        };

        // Calculate ROAS for a campaign
        const getCampaignROAS = (campaign) => {
            const spend = getMetric(campaign, 'spend');
            const revenue = getMetric(campaign, 'purchase_roas') ||
                getMetric(campaign, 'revenue') ||
                getMetric(campaign, 'website_purchase_roas') ||
                getMetric(campaign, 'value');
            if (spend > 0 && revenue > 0) {
                // If revenue is already ROAS (ratio), use it directly
                if (revenue < 100) return revenue;
                // Otherwise calculate
                return revenue / spend;
            }
            return 0;
        };

        // Check if aggregate metrics have real data
        const hasAggregateData = safeMetrics.spend > 0 || safeMetrics.revenue > 0 || safeMetrics.impressions > 0;

        // Check if individual campaigns have metrics data
        const campaignsWithSpend = campaigns.filter(c => {
            const spend = getMetric(c, 'spend');
            return spend > 0;
        });

        const context = {
            dateRange: dateRangeLabel,
            // Explicit data availability - prevents AI from hallucinating
            dataAvailability: {
                hasCampaigns: campaigns.length > 0,
                hasActiveCampaigns: activeCampaigns.length > 0,
                campaignCount: campaigns.length,
                activeCampaignCount: activeCampaigns.length,
                hasMetrics: hasAggregateData,
                hasIndividualCampaignMetrics: campaignsWithSpend.length > 0,
                campaignsWithMetricsCount: campaignsWithSpend.length
            },
            overallHealth: {
                activeCampaigns: activeCampaigns.length,
                totalCampaigns: campaigns.length,
                totalSpend: formatCurrency(safeMetrics.spend || 0),
                totalRevenue: formatCurrency(safeMetrics.revenue || 0),
                overallRoas: overallROAS ? overallROAS.toFixed(2) + 'x' : 'N/A'
            },
            // Include funnel summary if available
            funnel: knowledgeBase.funnel ? {
                summary: knowledgeBase.funnel.analysis?.summary,
                isHealthy: knowledgeBase.funnel.analysis?.isHealthy,
                issues: knowledgeBase.funnel.analysis?.issues?.slice(0, 2),
                spendDistribution: knowledgeBase.funnel.analysis?.spendDistribution
            } : null,
            // Include performance by stage
            performanceByStage: knowledgeBase.performanceByStage ? {
                awareness: {
                    count: knowledgeBase.performanceByStage.awareness.count,
                    health: knowledgeBase.performanceByStage.awareness.health
                },
                consideration: {
                    count: knowledgeBase.performanceByStage.consideration.count,
                    health: knowledgeBase.performanceByStage.consideration.health
                },
                conversion: {
                    count: knowledgeBase.performanceByStage.conversion.count,
                    health: knowledgeBase.performanceByStage.conversion.health,
                    roas: knowledgeBase.performanceByStage.conversion.roas
                }
            } : null,
            // Top insights
            topInsights: insights.slice(0, 3).map(i => ({
                type: i.type,
                priority: i.priority,
                title: i.title,
                action: i.action
            })),
            // Budget recommendations
            budgetRecommendations: knowledgeBase.budgetAnalysis?.recommendations?.slice(0, 2) || [],
            // Campaign data with metrics for AI to analyze
            campaigns: campaigns.slice(0, 10).map(c => {
                const roas = getCampaignROAS(c);
                return {
                    id: c.id,
                    name: c.name,
                    status: c.status,
                    objective: c.objective || c.normalizedObjective,
                    dailyBudget: c.dailyBudget || c.budget,
                    spend: formatCurrency(getMetric(c, 'spend')),
                    revenue: formatCurrency(getMetric(c, 'revenue') || getMetric(c, 'value')),
                    roas: roas > 0 ? roas.toFixed(2) + 'x' : 'N/A',
                    impressions: Math.round(getMetric(c, 'impressions')),
                    clicks: Math.round(getMetric(c, 'clicks')),
                    conversions: Math.round(getMetric(c, 'conversions') || getMetric(c, 'purchases'))
                };
            })
        };

        // Add warning if no data or metrics are missing
        if (campaigns.length === 0) {
            context.warning = 'NO_CAMPAIGN_DATA: No campaigns are loaded. Do not invent campaign names or IDs.';
        } else if (campaignsWithSpend.length === 0 && !hasAggregateData) {
            context.warning = 'METRICS_NOT_LOADED: Campaign performance metrics are not yet loaded. The campaign list shows $0 spend. Advise user to sync their campaigns to get real metrics.';
        }

        return context;
    };

    // Handle goal selection
    const handleGoalSelect = async (goal) => {
        console.log('=== GOAL SELECT TRIGGERED ===', goal);

        onSetGoal?.(goal);

        // Send initial message with goal using full context
        setSending(true);
        setError(null);

        const initialMessage = `I want to ${goal.objective.toLowerCase()}. What should I focus on?`;

        // Build context and log it for debugging
        const fullContext = buildFullContext();

        // Debug logging - always log for now to debug the issue
        console.log('[AIAdvisor] Goal selected:', goal.objective);
        console.log('[AIAdvisor] Campaigns available:', campaigns.length);
        console.log('[AIAdvisor] Active campaigns:', activeCampaigns.length);
        console.log('[AIAdvisor] Context being sent:', {
            dataAvailability: fullContext.dataAvailability,
            campaignsInContext: fullContext.campaigns?.length || 0,
            overallHealth: fullContext.overallHealth
        });
        console.log('[AIAdvisor] Full context:', fullContext);

        // Debug: Log the raw campaign data structure to find where metrics are stored
        if (campaigns.length > 0) {
            const firstCamp = campaigns[0];
            console.log('[AIAdvisor] Raw first campaign keys:', Object.keys(firstCamp));
            console.log('[AIAdvisor] Raw first campaign:', firstCamp);
            console.log('[AIAdvisor] currentMetrics:', firstCamp.currentMetrics);
            console.log('[AIAdvisor] insights:', firstCamp.insights);
            console.log('[AIAdvisor] budget:', firstCamp.budget);

            // Log all campaigns' spend values
            console.log('[AIAdvisor] All campaigns spend:', campaigns.map(c => ({
                name: c.name?.substring(0, 30),
                currentMetrics_spend: c.currentMetrics?.spend,
                insights_spend: c.insights?.data?.[0]?.spend
            })));
        }

        try {
            const response = await sendAdvisorMessage({
                message: initialMessage,
                conversationHistory: [],
                context: fullContext,
                userGoal: goal
            });

            const userMsg = createUserMessage(`I want to ${goal.objective.toLowerCase()}.`);
            const assistantMsg = createAssistantMessage(response);

            onMessagesChange?.([userMsg, assistantMsg]);
        } catch (err) {
            console.error('Failed to get advisor response:', err);
            setError('Failed to connect to advisor. Try again.');
        } finally {
            setSending(false);
        }
    };

    // Handle sending a message
    const handleSend = async () => {
        if (!inputValue.trim() || sending) return;

        const messageContent = inputValue.trim();
        console.log('=== HANDLE SEND TRIGGERED ===', messageContent);

        const userMsg = createUserMessage(messageContent);
        const newMessages = [...messages, userMsg];
        onMessagesChange?.(newMessages);
        setInputValue('');
        setSending(true);
        setError(null);

        try {
            // Use smart context builder - only sends relevant context based on question
            const smartContext = buildContext(messageContent);

            // Debug logging
            console.log('[AIAdvisor] Message sent:', messageContent);
            console.log('[AIAdvisor] Campaigns available:', campaigns.length);
            console.log('[AIAdvisor] Smart context:', smartContext);

            const response = await sendAdvisorMessage({
                message: messageContent,
                conversationHistory: messages.map(m => ({
                    role: m.role,
                    content: m.content
                })),
                context: smartContext,
                userGoal
            });

            const assistantMsg = createAssistantMessage(response);
            onMessagesChange?.([...newMessages, assistantMsg]);
            setShowInput(false);
        } catch (err) {
            console.error('Failed to send message:', err);
            setError('Failed to send message. Try again.');
        } finally {
            setSending(false);
        }
    };

    // Handle suggestion click
    const handleSuggestionClick = async (suggestion) => {
        setInputValue(suggestion);
        setShowInput(true);
        // Auto-send after a brief delay
        setTimeout(() => {
            const input = inputRef.current;
            if (input) {
                input.focus();
            }
        }, 100);
    };

    // Handle action approval
    const handleApproveAction = async (action) => {
        setActionPending(true);
        try {
            const result = await executeAction(action);

            // Update message with executed action
            const updatedMessages = messages.map(msg => {
                if (msg.proposedAction?.campaignId === action.campaignId) {
                    return {
                        ...msg,
                        proposedAction: null,
                        executedAction: {
                            ...action,
                            undoable: true,
                            undoAction: result.undoAction
                        }
                    };
                }
                return msg;
            });
            onMessagesChange?.(updatedMessages);
        } catch (err) {
            console.error('Failed to execute action:', err);
            setError(`Action failed: ${err.message}`);
        } finally {
            setActionPending(false);
        }
    };

    // Handle action skip
    const handleSkipAction = () => {
        const updatedMessages = messages.map(msg => {
            if (msg.proposedAction) {
                return { ...msg, proposedAction: null };
            }
            return msg;
        });
        onMessagesChange?.(updatedMessages);
    };

    // Navigate to Deep Dive
    const handleDeepDive = () => {
        navigate('/command-center/advisor');
    };

    // No goal set - show goal selection
    if (!userGoal) {
        return (
            <GoalSelector
                onSelect={handleGoalSelect}
                loading={sending}
                isExpanded={isExpanded}
                onToggleExpand={() => setIsExpanded(!isExpanded)}
                campaignCount={campaigns.length}
                isDataLoading={loading}
            />
        );
    }

    // Loading state
    if (loading && messages.length === 0) {
        return <LoadingState />;
    }

    // Error state with no messages
    if (error && messages.length === 0) {
        return (
            <ErrorState
                error={error}
                onRetry={() => {
                    setError(null);
                    handleGoalSelect(userGoal);
                }}
            />
        );
    }

    return (
        <div style={{
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08), rgba(124, 58, 237, 0.02))',
            border: '1px solid rgba(124, 58, 237, 0.2)',
            borderRadius: 'var(--radius-lg)'
        }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-4)',
                    cursor: 'pointer'
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--gradient-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Sparkles size={20} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-primary)'
                        }}>
                            AI Advisor
                        </span>
                        {userGoal && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSetGoal?.(null);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    background: 'var(--color-surface)',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                <Target size={10} />
                                {userGoal.objective}
                                <X size={10} style={{ marginLeft: '2px' }} />
                            </button>
                        )}
                    </div>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeepDive();
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-text-secondary)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500,
                        cursor: 'pointer'
                    }}
                >
                    Deep Dive
                    <ArrowRight size={12} />
                </button>
                {isExpanded ? (
                    <ChevronUp size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                ) : (
                    <ChevronDown size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                )}
            </div>

            {/* Content */}
            {isExpanded && (
                <div style={{
                    padding: '0 var(--space-4) var(--space-4) var(--space-4)',
                    overflow: 'visible'
                }}>
                    {/* Latest message */}
                    {latestAssistantMessage && (
                        <div style={{
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-4)',
                            position: 'relative'
                        }}>
                            {/* Chat bubble tail */}
                            <div style={{
                                position: 'absolute',
                                top: '-8px',
                                left: '30px',
                                width: 0,
                                height: 0,
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderBottom: '8px solid var(--color-surface)'
                            }} />

                            <div style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-primary)',
                                lineHeight: 1.6,
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                            }}>
                                {renderMarkdown(latestAssistantMessage.content)}
                            </div>

                            {/* Proposed action */}
                            {latestAssistantMessage.proposedAction && (
                                <QuickAction
                                    action={latestAssistantMessage.proposedAction}
                                    onApprove={handleApproveAction}
                                    onSkip={handleSkipAction}
                                    isPending={actionPending}
                                />
                            )}

                            {/* Executed action */}
                            {latestAssistantMessage.executedAction && (
                                <ExecutedActionBadge action={latestAssistantMessage.executedAction} />
                            )}
                        </div>
                    )}

                    {/* Sending indicator */}
                    {sending && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginTop: 'var(--space-3)',
                            color: 'var(--color-text-tertiary)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <Loader2 size={14} className="spin" />
                            Thinking...
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div style={{
                            marginTop: 'var(--space-3)',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'rgba(239, 68, 68, 0.1)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-error)'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Suggestions */}
                    {latestAssistantMessage?.suggestions?.length > 0 && !showInput && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--space-2)',
                            marginTop: 'var(--space-3)'
                        }}>
                            {latestAssistantMessage.suggestions.map((suggestion, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    style={{
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'var(--color-surface)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: 'var(--radius-full)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        cursor: 'pointer',
                                        transition: 'all var(--transition-fast)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--color-primary)';
                                        e.currentTarget.style.color = 'var(--color-primary)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--color-border)';
                                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                                    }}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input area */}
                    {showInput ? (
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-2)',
                            marginTop: 'var(--space-3)'
                        }}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                    if (e.key === 'Escape') {
                                        setShowInput(false);
                                        setInputValue('');
                                    }
                                }}
                                placeholder="Ask the advisor..."
                                disabled={sending}
                                style={{
                                    flex: 1,
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: 'var(--color-surface)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-primary)',
                                    outline: 'none'
                                }}
                            />
                            <button
                                onClick={handleSend}
                                disabled={sending || !inputValue.trim()}
                                style={{
                                    padding: 'var(--space-2)',
                                    background: 'var(--color-primary)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    color: 'white',
                                    cursor: sending || !inputValue.trim() ? 'not-allowed' : 'pointer',
                                    opacity: sending || !inputValue.trim() ? 0.5 : 1
                                }}
                            >
                                <Send size={16} />
                            </button>
                            <button
                                onClick={() => {
                                    setShowInput(false);
                                    setInputValue('');
                                }}
                                style={{
                                    padding: 'var(--space-2)',
                                    background: 'transparent',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    color: 'var(--color-text-tertiary)',
                                    cursor: 'pointer'
                                }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowInput(true)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginTop: 'var(--space-3)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--color-text-tertiary)',
                                fontSize: 'var(--font-size-xs)',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--color-primary)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--color-text-tertiary)';
                            }}
                        >
                            <MessageSquare size={14} />
                            Ask something...
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * GoalSelector - Initial state when no goal is set
 */
const GoalSelector = ({ onSelect, loading, isExpanded, onToggleExpand, campaignCount = 0, isDataLoading = false }) => {
    const hasNoCampaigns = !isDataLoading && campaignCount === 0;

    return (
        <div style={{
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08), rgba(124, 58, 237, 0.02))',
            border: '1px solid rgba(124, 58, 237, 0.2)',
            borderRadius: 'var(--radius-lg)'
        }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-4)',
                    cursor: 'pointer'
                }}
                onClick={onToggleExpand}
            >
                <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--gradient-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Sparkles size={20} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-primary)'
                    }}>
                        AI Advisor
                    </span>
                    {campaignCount > 0 && (
                        <span style={{
                            marginLeft: 'var(--space-2)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            ({campaignCount} campaigns)
                        </span>
                    )}
                </div>
                {isExpanded ? (
                    <ChevronUp size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                ) : (
                    <ChevronDown size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                )}
            </div>

            {isExpanded && (
                <div style={{
                    padding: '0 var(--space-4) var(--space-4) var(--space-4)'
                }}>
                    <div style={{
                        background: 'var(--color-surface)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--space-4)',
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '-8px',
                            left: '30px',
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderBottom: '8px solid var(--color-surface)'
                        }} />

                        {hasNoCampaigns && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-3)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'rgba(245, 158, 11, 0.1)',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-warning)'
                            }}>
                                <AlertCircle size={14} />
                                No campaigns loaded. Connect your ad account to get personalized advice.
                            </div>
                        )}

                        <p style={{
                            margin: '0 0 var(--space-3) 0',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-primary)',
                            lineHeight: 1.6
                        }}>
                            {hasNoCampaigns
                                ? "I can still help with general marketing strategy. What's your goal?"
                                : "What's your main goal right now? I'll tailor my advice to help you achieve it."
                            }
                        </p>

                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--space-2)'
                        }}>
                            {GOAL_PRESETS.map((goal) => (
                                <button
                                    key={goal.id}
                                    onClick={() => onSelect(goal)}
                                    disabled={loading}
                                    style={{
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'var(--color-surface-elevated)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: 'var(--radius-full)',
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-primary)',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        opacity: loading ? 0.5 : 1,
                                        transition: 'all var(--transition-fast)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!loading) {
                                            e.currentTarget.style.borderColor = 'var(--color-primary)';
                                            e.currentTarget.style.background = 'rgba(124, 58, 237, 0.1)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--color-border)';
                                        e.currentTarget.style.background = 'var(--color-surface-elevated)';
                                    }}
                                >
                                    {goal.label}
                                </button>
                            ))}
                        </div>

                        {loading && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginTop: 'var(--space-3)',
                                color: 'var(--color-text-tertiary)',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                <Loader2 size={14} className="spin" />
                                Setting up your advisor...
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * QuickAction - Compact action proposal for Quick Mode
 */
const QuickAction = ({ action, onApprove, onSkip, isPending }) => {
    return (
        <div style={{
            marginTop: 'var(--space-3)',
            padding: 'var(--space-3)',
            background: 'rgba(124, 58, 237, 0.05)',
            border: '1px solid rgba(124, 58, 237, 0.15)',
            borderRadius: 'var(--radius-md)'
        }}>
            <div style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-2)'
            }}>
                {action.description}
            </div>
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)'
            }}>
                <button
                    onClick={() => onApprove(action)}
                    disabled={isPending}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: 'var(--space-1) var(--space-2)',
                        background: 'var(--color-primary)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        color: 'white',
                        fontSize: 'var(--font-size-xs)',
                        cursor: isPending ? 'not-allowed' : 'pointer',
                        opacity: isPending ? 0.7 : 1
                    }}
                >
                    {isPending ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
                    Approve
                </button>
                <button
                    onClick={onSkip}
                    disabled={isPending}
                    style={{
                        padding: 'var(--space-1) var(--space-2)',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-text-tertiary)',
                        fontSize: 'var(--font-size-xs)',
                        cursor: 'pointer'
                    }}
                >
                    Skip
                </button>
            </div>
        </div>
    );
};

/**
 * ExecutedActionBadge - Shows completed action
 */
const ExecutedActionBadge = ({ action }) => {
    return (
        <div style={{
            marginTop: 'var(--space-3)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            background: 'rgba(34, 197, 94, 0.1)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-success)'
        }}>
            <Check size={12} />
            {action.description}
        </div>
    );
};

/**
 * LoadingState
 */
const LoadingState = () => (
    <div style={{
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(124, 58, 237, 0.02))',
        border: '1px solid rgba(124, 58, 237, 0.2)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)'
    }}>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)'
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-full)',
                background: 'var(--gradient-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Sparkles size={20} style={{ color: 'white' }} />
            </div>
            <div style={{
                flex: 1,
                height: '20px',
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-md)',
                animation: 'pulse 2s infinite'
            }} />
        </div>
    </div>
);

/**
 * ErrorState
 */
const ErrorState = ({ error, onRetry }) => (
    <div style={{
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.02))',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)'
    }}>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)'
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-full)',
                background: 'rgba(239, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <AlertCircle size={20} style={{ color: 'var(--color-error)' }} />
            </div>
            <div style={{ flex: 1 }}>
                <p style={{
                    margin: 0,
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)'
                }}>
                    {error}
                </p>
            </div>
            <button
                onClick={onRetry}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'var(--color-primary)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    color: 'white',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    cursor: 'pointer'
                }}
            >
                <RefreshCw size={14} />
                Retry
            </button>
        </div>
    </div>
);

export default AIAdvisorPanel;
